import { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation, Autoplay } from "swiper/modules";
import type { SwiperRef } from "swiper/react";
import { useTranslation } from "../../../../../components/Translation";

export type MediaItem = {
  type: "image" | "video" | "pdf";
  src: string;
  alt: string;
};

export type ModalContent = {
  title: string;
  description: string;
  media: MediaItem[];
};

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  content: ModalContent;
  modalId: string;
}

export default function Modal({ isOpen, onClose, content, modalId }: ModalProps) {
  const swiperRef = useRef<SwiperRef>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [verticalMediaIndexes, setVerticalMediaIndexes] = useState<number[]>([]);
  const { t } = useTranslation();

  const visualMedia = content.media.filter(item => item.type === "image" || item.type === "video");

  const goPrev = () => swiperRef.current?.swiper?.slidePrev();
  const goNext = () => swiperRef.current?.swiper?.slideNext();

  const togglePause = () => {
    if (!swiperRef.current?.swiper) return;
    const swiper = swiperRef.current.swiper;
    if (isPaused) {
      swiper.autoplay.start();
    } else {
      swiper.autoplay.stop();
    }
    setIsPaused(!isPaused);
  };

  const openPdf = () => {
    const pdfItem = content.media.find(item => item.type === "pdf");
    if (pdfItem) window.open(pdfItem.src, "_blank");
  };

  // Cerrar con Escape y bloquear scroll del body
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && visualMedia.length > 1) {
      const timer = setTimeout(() => {
        swiperRef.current?.swiper?.autoplay.start();
        setIsPaused(false);
      }, 100);
      return () => clearTimeout(timer);
    }
    return () => swiperRef.current?.swiper?.autoplay.stop();
  }, [isOpen, visualMedia.length]);

  useEffect(() => {
    const detectVerticals = async () => {
      const verticals: number[] = [];

      await Promise.all(
        visualMedia.map((item, index) => {
          if (item.type === "image") {
            return new Promise<void>(resolve => {
              const img = new Image();
              img.onload = () => {
                if (img.height > img.width) verticals.push(index);
                resolve();
              };
              img.onerror = () => resolve();
              img.src = item.src;
            });
          } else if (item.type === "video") {
            return new Promise<void>(resolve => {
              const video = document.createElement("video");
              video.onloadedmetadata = () => {
                if (video.videoHeight > video.videoWidth) verticals.push(index);
                resolve();
              };
              video.onerror = () => resolve();
              video.src = item.src;
            });
          }
          return Promise.resolve();
        })
      );

      setVerticalMediaIndexes(verticals);
    };

    if (isOpen) detectVerticals();
  }, [isOpen, visualMedia]);

  if (!isOpen) return null;

  const getMediaClass = (index: number) => {
    const isVertical = verticalMediaIndexes.includes(index);
    return isVertical
      ? "w-full h-auto max-h-[40vh] sm:max-h-[50vh] lg:max-h-[70vh]"
      : "w-full h-auto max-h-[60vh] sm:max-h-[70vh]";
  };

  return (
    // Backdrop — click fuera cierra el modal
    <div
      id={modalId}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm w-screen h-screen p-2 sm:p-4"
      onClick={onClose}
    >
      {/* Contenedor del modal — detiene propagación para no cerrar al hacer click dentro */}
      <div
        className="bg-white rounded-lg w-full max-w-[95vw] sm:max-w-xl md:max-w-2xl lg:max-w-6xl h-auto max-h-[90vh] overflow-hidden relative shadow-lg flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <span
          className="absolute top-2 right-2 text-gray-600 cursor-pointer text-2xl sm:text-3xl font-bold z-50"
          onClick={onClose}
        >
          ×
        </span>

        <div className="grid grid-cols-1 lg:grid-cols-3 p-4 sm:p-6 gap-4 sm:gap-6 h-full overflow-hidden">
          <div className="lg:col-span-1 overflow-y-auto">
            <h2 className="text-xl sm:text-2xl text-center font-bold mb-2 sm:mb-4">
              {t(`laboratorio.${content.title}`)}
            </h2>
            <p className="text-sm sm:text-base text-justify mb-4 leading-relaxed text-gray-700">
              {t(`laboratorio.${content.description}`)}
            </p>
            {content.media.some(item => item.type === "pdf") && (
              <button
                onClick={openPdf}
                className="mt-4 w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition-colors"
              >
                Ver Documento
              </button>
            )}
          </div>

          <div className="lg:col-span-2 relative h-full flex flex-col">
            {visualMedia.length === 1 ? (
              <div className="flex-1 flex items-center justify-center overflow-hidden">
                {visualMedia[0].type === "video" ? (
                  <video
                    className={getMediaClass(0) + " object-contain rounded-lg shadow-md"}
                    controls
                  >
                    <source src={visualMedia[0].src} type="video/mp4" />
                    Tu navegador no soporta video HTML5.
                  </video>
                ) : (
                  <img
                    src={visualMedia[0].src}
                    alt={visualMedia[0].alt}
                    className={getMediaClass(0) + " object-contain rounded-lg shadow-md"}
                  />
                )}
              </div>
            ) : visualMedia.length > 1 ? (
              <section className="relative w-full h-full overflow-hidden">
                <Swiper
                  ref={swiperRef}
                  modules={[Pagination, Navigation, Autoplay]}
                  slidesPerView={1}
                  spaceBetween={10}
                  autoplay={{ delay: 5000, disableOnInteraction: false }}
                  pagination={{
                    el: `.${modalId}-pagination`,
                    clickable: true,
                    type: "bullets",
                    bulletElement: "button",
                    bulletClass: "swiper-pagination-bullet swiper-pagination-bullet-cards w-5 h-5 sm:w-5 sm:h-5",
                    bulletActiveClass: "swiper-pagination-bullet-active",
                  }}
                  className="w-full h-full"
                >
                  {visualMedia.map((item, index) => (
                    <SwiperSlide key={index} className="flex items-center justify-center h-full">
                      <div className="w-full h-full flex items-center justify-center">
                        {item.type === "image" ? (
                          <img
                            src={item.src}
                            alt={item.alt}
                            className={`${getMediaClass(index)} object-contain rounded-lg shadow-md`}
                          />
                        ) : (
                          <video
                            className={`${getMediaClass(index)} object-contain rounded-lg shadow-md`}
                            controls
                          >
                            <source src={item.src} type="video/mp4" />
                            Tu navegador no soporta video HTML5.
                          </video>
                        )}
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>

                <button
                  onClick={goPrev}
                  className="absolute top-1/2 left-2 sm:left-4 transform -translate-y-1/2 bg-gray-800 bg-opacity-70 text-white p-1 sm:p-2 text-lg sm:text-xl rounded-full hover:bg-gray-700 focus:outline-none z-10"
                >
                  ❮
                </button>
                <button
                  onClick={goNext}
                  className="absolute top-1/2 right-2 sm:right-4 transform -translate-y-1/2 bg-gray-800 bg-opacity-70 text-white p-1 sm:p-2 text-lg sm:text-xl rounded-full hover:bg-gray-700 focus:outline-none z-10"
                >
                  ❯
                </button>
              </section>
            ) : null}

            {visualMedia.length > 1 && (
              <div className="flex items-center justify-center gap-4 sm:gap-5 p-4 pt-0">
                <button
                  onClick={togglePause}
                  className="bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-1 transition shadow-md"
                  aria-label={isPaused ? "Reanudar carrusel" : "Pausar carrusel"}
                >
                  <img
                    src={isPaused ? "/img/iconos/play.png" : "/img/iconos/pause.png"}
                    alt={isPaused ? "Play" : "Pause"}
                    className="w-3 h-3 sm:w-3 sm:h-3"
                  />
                </button>
                <div className="flex items-center scale-125 sm:scale-150 pt-4">
                  <div className={`${modalId}-pagination`} style={{ position: "relative" }} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}